import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckCircle,
  Clock,
  Database,
  Download,
  Lock,
  SkipForward,
  ChevronDown,
  ChevronRight,
  Paperclip,
  Image as ImageIcon,
  FileIcon,
  Loader2,
  Eye,
  X,
} from 'lucide-react';
import { taskPipelineService, ConversationAttachmentSummary } from '@/services/taskPipeline.service';
import { CustomerTaskProgress } from '@/types';
import toast from 'react-hot-toast';

interface SimulationTaskPanelProps {
  conversationId: string | null;
  refreshTrigger?: number;
}

export const SimulationTaskPanel: React.FC<SimulationTaskPanelProps> = ({ conversationId, refreshTrigger }) => {
  const [progressList, setProgressList] = useState<CustomerTaskProgress[]>([]);
  const [attachments, setAttachments] = useState<ConversationAttachmentSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Document viewer modal
  const [viewingDoc, setViewingDoc] = useState<CustomerTaskProgress | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Change tracking for highlights
  const [changedIds, setChangedIds] = useState<Set<string>>(new Set());
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const prevDataRef = useRef<Map<string, CustomerTaskProgress>>(new Map());

  const loadProgress = useCallback(async () => {
    if (!conversationId) return;
    try {
      const [data, atts] = await Promise.all([
        taskPipelineService.getProgressForConversation(conversationId),
        taskPipelineService.getAttachmentsForConversation(conversationId).catch(() => []),
      ]);

      // Detect changes for highlighting
      const newChangedIds = new Set<string>();
      const newChangedFields = new Set<string>();
      const prevMap = prevDataRef.current;

      for (const item of data) {
        const prev = prevMap.get(item.id);
        if (!prev) {
          if (prevMap.size > 0) newChangedIds.add(item.id);
        } else {
          if (prev.status !== item.status) {
            newChangedIds.add(item.id);
          }
          const prevKeys = Object.keys(prev.collected_data || {});
          const newKeys = Object.keys(item.collected_data || {});
          if (newKeys.length > prevKeys.length) {
            newChangedIds.add(item.id);
            for (const k of newKeys) {
              if (!prev.collected_data?.[k]) {
                newChangedFields.add(`${item.id}:${k}`);
              }
            }
          }
          if (item.generated_content && item.generated_content !== prev.generated_content) {
            newChangedIds.add(item.id);
          }
        }
      }

      const newMap = new Map<string, CustomerTaskProgress>();
      for (const item of data) newMap.set(item.id, item);
      prevDataRef.current = newMap;

      if (newChangedIds.size > 0) {
        setChangedIds(newChangedIds);
        setChangedFields(newChangedFields);
        setTimeout(() => {
          setChangedIds(new Set());
          setChangedFields(new Set());
        }, 2500);
      }

      setProgressList(data);
      setAttachments(atts);
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setIsLoading(true);
    loadProgress();
  }, [conversationId, loadProgress]);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      loadProgress();
    }
  }, [refreshTrigger, loadProgress]);

  useEffect(() => {
    if (!conversationId) return;
    const interval = setInterval(loadProgress, 4000);
    return () => clearInterval(interval);
  }, [conversationId, loadProgress]);

  // Keep viewingDoc synced with latest data
  useEffect(() => {
    if (viewingDoc) {
      const updated = progressList.find(p => p.id === viewingDoc.id);
      if (updated && updated.generated_content !== viewingDoc.generated_content) {
        setViewingDoc(updated);
      }
    }
  }, [progressList, viewingDoc]);

  const handleDownload = async (progressId: string, format: 'docx' | 'pdf', taskName?: string) => {
    setDownloadingId(progressId);
    try {
      const blob = await taskPipelineService.downloadDocument(progressId, format);
      const url = URL.createObjectURL(blob);
      const safeName = (taskName || 'document').replace(/[^a-zA-Z0-9_-]/g, '_');

      if (format === 'pdf') {
        const win = window.open(url, '_blank');
        if (win) win.onload = () => win.print();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safeName}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch {
      toast.error(`Download failed`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewAttachment = async (att: ConversationAttachmentSummary) => {
    try {
      const full = await taskPipelineService.getAttachment(att.id);
      const byteChars = atob(full.content_base64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes], { type: full.mime_type });
      const url = URL.createObjectURL(blob);
      if (att.mime_type.startsWith('image/')) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error('Failed to load file');
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Done', pill: 'bg-green-100 text-green-700' };
      case 'in_progress':
        return { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Active', pill: 'bg-blue-100 text-blue-700' };
      case 'blocked':
        return { icon: Lock, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Blocked', pill: 'bg-orange-100 text-orange-700' };
      case 'skipped':
        return { icon: SkipForward, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Skipped', pill: 'bg-gray-100 text-gray-600' };
      default:
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-50', label: 'Pending', pill: 'bg-gray-100 text-gray-500' };
    }
  };

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        Waiting for conversation...
      </div>
    );
  }

  if (isLoading && progressList.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (progressList.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm p-4 text-center">
        <div>
          <Database className="w-6 h-6 mx-auto mb-2 text-gray-300" />
          <p>No task pipeline data yet</p>
        </div>
      </div>
    );
  }

  const completedCount = progressList.filter(p => p.status === 'completed').length;
  const totalCount = progressList.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <>
      <div className="h-full flex flex-col">
        {/* Header with progress bar */}
        <div className="px-4 py-3 border-b-2 border-[#1a1a2e] bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-blue-600" />
              <span className="font-semibold text-gray-900 text-xs">Task Pipeline</span>
            </div>
            <span className="text-xs font-bold text-gray-700">{completedCount}/{totalCount}</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${progressPercent === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="px-3 pt-2 pb-1 flex-shrink-0">
            <div className="bg-purple-50 border-2 border-[#1a1a2e] rounded-xl px-3 py-2">
              <div className="flex items-center gap-1.5 mb-1">
                <Paperclip className="w-3 h-3 text-purple-600" />
                <span className="text-[10px] font-semibold text-purple-700 uppercase">Files ({attachments.length})</span>
              </div>
              <div className="space-y-1">
                {attachments.map((att) => (
                  <div key={att.id} className="flex items-center gap-1.5 group">
                    {att.mime_type.startsWith('image/') ? <ImageIcon className="w-3 h-3 text-purple-600" /> : <FileIcon className="w-3 h-3 text-purple-600" />}
                    <span className="text-xs text-purple-700 truncate flex-1">{att.filename}</span>
                    <button
                      onClick={() => handleViewAttachment(att)}
                      className="p-0.5 text-purple-400 hover:text-purple-700 transition-colors"
                      title={att.mime_type.startsWith('image/') ? 'View' : 'Download'}
                    >
                      {att.mime_type.startsWith('image/') ? <Eye className="w-3 h-3" /> : <Download className="w-3 h-3" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Task List */}
        <div className="flex-1 overflow-y-auto">
          {progressList.map((progress) => {
            const sc = getStatusConfig(progress.status);
            const StatusIcon = sc.icon;
            const isExpanded = expandedId === progress.id;
            const isDocTask = progress.task_type === 'document_creation';
            const collectedEntries = Object.entries(progress.collected_data || {});
            const isHighlighted = changedIds.has(progress.id);

            return (
              <div
                key={progress.id}
                className={`border-b-2 border-[#1a1a2e] transition-colors duration-500 ${isHighlighted ? 'bg-yellow-50' : ''}`}
              >
                <div
                  className="flex items-center gap-2.5 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : progress.id)}
                >
                  <div className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${sc.bg}`}>
                    <StatusIcon className={`h-3.5 w-3.5 ${sc.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate">{progress.task_name || 'Task'}</p>
                    <span className={`text-[10px] font-medium px-1.5 py-0 rounded-full ${sc.pill}`}>
                      {sc.label}
                    </span>
                  </div>

                  {/* Quick action icons on the row */}
                  {isDocTask && progress.generated_content && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setViewingDoc(progress); }}
                      className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                      title="View document"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                  )}

                  <div className="flex-shrink-0">
                    {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-3 space-y-2">
                    {/* Collected Data */}
                    {collectedEntries.length > 0 && (
                      <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                        <div className="px-2.5 py-1.5 bg-gray-50 border-b-2 border-[#1a1a2e]">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase">Collected Data</p>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {collectedEntries.map(([key, value]) => {
                            const fieldKey = `${progress.id}:${key}`;
                            const isFieldHighlighted = changedFields.has(fieldKey);
                            const strValue = String(value);
                            const isAttachment = strValue.startsWith('attachment:');

                            return (
                              <div
                                key={key}
                                className={`px-2.5 py-1.5 flex justify-between items-center transition-colors duration-500 ${isFieldHighlighted ? 'bg-green-50' : ''}`}
                              >
                                <span className="text-[11px] text-gray-500">{key}</span>
                                {isAttachment ? (
                                  <span className="text-[11px] text-purple-500 flex items-center gap-1">
                                    <Paperclip className="w-3 h-3" /> File
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-gray-900 font-medium text-right truncate max-w-[55%]">{strValue}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Document Preview + Actions */}
                    {isDocTask && progress.generated_content && (
                      <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                        <div className="px-2.5 py-1.5 bg-gray-50 border-b-2 border-[#1a1a2e] flex items-center justify-between">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase">Document</p>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setViewingDoc(progress)}
                              className="p-0.5 text-blue-500 hover:text-blue-700 transition-colors"
                              title="View full document"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDownload(progress.id, 'docx', progress.task_name)}
                              disabled={downloadingId === progress.id}
                              className="p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
                              title="Download DOCX"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <div
                          className="p-2.5 max-h-32 overflow-y-auto cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setViewingDoc(progress)}
                        >
                          <pre className="text-[11px] text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                            {progress.generated_content.substring(0, 500)}{progress.generated_content.length > 500 ? '...' : ''}
                          </pre>
                        </div>
                        {progress.approved_at && (
                          <div className="px-2.5 py-1 border-t-2 border-[#1a1a2e] bg-green-50">
                            <span className="text-[10px] text-green-600 font-semibold">Approved</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Full Document Viewer Modal */}
      {viewingDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setViewingDoc(null)}>
          <div className="bg-white rounded-xl shadow-[5px_5px_0px_#1a1a2e] w-full max-w-2xl mx-4 flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#1a1a2e] flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{viewingDoc.task_name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-400">
                    {viewingDoc.generated_content?.length || 0} characters
                  </span>
                  {viewingDoc.approved_at && (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Approved</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(viewingDoc.id, 'docx', viewingDoc.task_name)}
                  disabled={downloadingId === viewingDoc.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  DOCX
                </button>
                <button
                  onClick={() => handleDownload(viewingDoc.id, 'pdf', viewingDoc.task_name)}
                  disabled={downloadingId === viewingDoc.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Print
                </button>
                <button
                  onClick={() => setViewingDoc(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed font-sans">
                {viewingDoc.generated_content}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
